import {Component,Input,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {Frequence,Genre,ModeParticipants,Prestation,TypeAssociation,TypePrestation,TypeRemboursement} from "@domain/prestation/prestation";
import {PrestationService} from "@services/admin/frais/prestation.service";
import {TranslateService} from "@ngx-translate/core";
import {NgForm} from "@angular/forms";
import {TypeDistance,Unite} from "@domain/prestation/unite";
import {first} from "rxjs/operators";
import {Referentiel} from "@domain/admin/referentiels/referentiel";
import {TypeBareme} from "@domain/prestation/TypeBareme";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Devise} from "@domain/settings/devise";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {DisplayCumul,TypeCumul} from "@domain/prestation/prest-cumul";
import {AddCumulComponent} from "@components/admin/frais/prestations/prestation-detail/regles-gestion/add-cumul.component";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {DisplayCompte,TypeCompte} from "@domain/comptabilite/compte";
import {AddCompteExterneComponent} from "@components/admin/frais/prestations/prestation-detail/regles-gestion/add-compte-externe.component";
import {Domaine} from "@domain/taxe/domaine";
import {Observable,Subscription} from "rxjs";
import {BaremePopinComponent} from "@components/admin/frais/prestations/prestation-detail/regles-gestion/bareme-popin.component";
import {PrestationDetailService} from "@components/admin/frais/prestations/prestation-detail/prestation-detail.service";
import {MontantPopinComponent} from "@components/admin/frais/prestations/prestation-detail/regles-gestion/montant-popin/montant-popin.component";
import {Montant} from "@domain/prestation/montant";

/**
 * Composant de l'onglet règles de gestion d'une prestation
 */
@Component({
	selector: 'prestation-regles-gestion',
	host: {'data-test-id': 'prestation-regles-gestion'},
	templateUrl: './prestation-regles-gestion.component.html'
})
export class PrestationReglesGestionComponent implements OnInit, OnDestroy {
	/** Types prestations */
	protected readonly TypePrestation = TypePrestation;

	/** Types distances */
	protected readonly TypeDistance = TypeDistance;

	/** Types barèmes */
	protected readonly TypeBareme = TypeBareme;

	/** Genre */
	protected readonly Genre = Genre;

	/** Unité < Aucune > */
	private readonly uniteAucune: Unite = {
		idUnite: 0,
		libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.aucune"),
		typeDistance: TypeDistance.TYPE_DISTANCE_NONE
	} as Unite;

	/** Cumul < Aucun > */
	private readonly cumulAucun: DisplayCumul = {
		idCumul: 0,
		libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.aucun"),
		display: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.aucun")
	} as DisplayCumul;

	/** Prestation concernée */
	private _prestation: Prestation;
	@Input() set prestation(prestation: Prestation) { this._prestation = prestation; this.syncPrestation(); };
	get prestation(): Prestation { return this._prestation; }

	/** Flag indiquant si la page est chargée */
	@Input() isLoaded: boolean;

	/** Conversion du type de remboursement en booléen */
	get isRemboursable(): boolean { return this.prestation?.typeRemboursement === TypeRemboursement.REMBOURSABLE; }
	set isRemboursable(value: boolean) { this.prestation.typeRemboursement = (value ? TypeRemboursement.REMBOURSABLE : TypeRemboursement.NON_REMBOURSABLE); }

	/** Liste des types possibles pour le select */
	listeTypes: Array<any> = [
		{type: TypePrestation.REEL,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.type.reel") },
		{type: TypePrestation.PLAFOND,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.type.plafond") },
		{type: TypePrestation.FORFAIT,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.type.forfait") },
	];

	/** Liste des unités possibles */
	listeUnite: Unite[] = [this.uniteAucune];

	/** Liste des domaines prestations possibles */
	listeDomainePrestation: Referentiel[] = [{
		id: 0, libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.aucun")
	} as Referentiel];

	/** Domaine prestation sélectionné */
	selectedDomainePrestation: Referentiel;

	/** Le montant à l'initialisation de la prestation */
	montantCached: number;

	/** La date d'application à l'initialisation de la prestation */
	dateApplicationCached: Date;

	/** Liste des barèmes possibles pour le select */
	listeBaremes: Array<any> = [
		{ id: TypeBareme.SANS,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.bareme.sans") },
		{ id: TypeBareme.BAREME_FISCAL,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.bareme.fiscal") },
		{ id: TypeBareme.BAREME_SNCF,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.remboursement.bareme.sncf") }
	];

	/** Liste des devises */
	listeDevises: Array<Devise> = [];

	/** Liste des fréquences possibles pour le select */
	listeFrequences: Array<any> = [
		{ id: Frequence.AUCUNE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.frequence.0") },
		{ id: Frequence.QUOTIDIENNE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.frequence.1") },
		{ id: Frequence.HEBDOMADAIRE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.frequence.2") },
		{ id: Frequence.MENSUELLE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.frequence.3") },
		{ id: Frequence.ANNUELLE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.frequence.4") },
		{ id: Frequence.EVENEMENT,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.frequence.5") }
	];

	/** Liste des types d'associations possibles pour le select */
	listeTypeAssociation: Array<any> = [
		{ id: TypeAssociation.AUCUNE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.typeAssociation.0") },
		{ id: TypeAssociation.PRINCIPALE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.typeAssociation.1") },
		{ id: TypeAssociation.SECONDAIRE,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.typeAssociation.2") }
	];

	/** Liste des cumuls possibles */
	listeCumul: DisplayCumul[] = [this.cumulAucun];

	/** Cumul sélectionné */
	selectedCumul: DisplayCumul;

	/** Liste des usages possibles pour le select */
	listeUsage: Array<any> = [
		{ code: Genre.STANDARD,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.invitations.usage.standard") },
		{ code: Genre.RECEPTION,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.invitations.usage.reception") }
	];

	/** Liste des comptes externes possibles */
	listeCompteExterne: DisplayCompte[] = [{
		numero: null,
		libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.invitations.aucun"),
		type: TypeCompte.COMPTE_TIERS
	} as DisplayCompte];

	/** Compte externe sélectionné */
	selectedCompteExterne: DisplayCompte;

	/** Liste des modes participant possibles pour le select */
	listeModeParticipant: Array<any> = [
		{ code: ModeParticipants.TOUS,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.invitations.gestionInvites.0") },
		{ code: ModeParticipants.INTERNES,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.invitations.gestionInvites.1") },
		{ code: ModeParticipants.EXTERNES,libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.invitations.gestionInvites.2") }
	];

	/** Liste des domaines taxe possibles */
	listeDomaineTaxe: Domaine[] = [{
		idDomaine: 0,
		libelle: this.translateService.instant("admin.frais.prestations.detail.reglesGestion.invitations.aucun")
	} as Domaine];

	/** Domaine taxe sélectionné */
	selectedDomaineTaxe: Domaine;

	/** Domaine alternatif sélectionné */
	selectedDomaineAlternatif: Domaine;

	/** Évènement de rechargement des données de la page */
	@Input() reloadPageDataEvent: Observable<void>;

	/** Formulaire du composant */
	@ViewChild('form') form: NgForm;

	/** Souscription */
	souscription: Subscription;

	/**
	 * Constructeur
	 */
	constructor(private prestationService: PrestationService,
				private translateService: TranslateService,
				private store: Store<AppState>,
				private prestationDetailService: PrestationDetailService,
				private matDialog: MatDialog) {}

	/**
	 * Initialisation du composant
	 */
	ngOnInit() {
		//Récupération des unités possibles
		this.prestationService.listeUnite().pipe(first()).subscribe(f => {
			//Stockage des unités
			this.listeUnite = this.listeUnite.concat(f);

			//Synchronisation de l'unité
			this.syncUnite();
		});

		//Récupération des domaines prestations possibles
		this.prestationService.listeDomainePrestation().pipe(first()).subscribe(f => {
			//Stockage des domaines prestations
			this.listeDomainePrestation = this.listeDomainePrestation.concat(f);

			//Synchronisation du domaine prestation
			this.syncDomainePrestation();
		});

		//Récupération de la liste des devises
		this.store.select(state => state.settings?.Global).pipe(filterFirstNotNull())
					.subscribe((settings) => { this.listeDevises = settings.listeDevises; });

		//Récupération des cumuls possibles
		this.prestationService.listeCumul().pipe(first()).subscribe(f => {
			//Stockage des cumuls
			this.listeCumul = this.listeCumul.concat(f);

			//Synchronisation du cumul
			this.syncCumul();
		});

		//Récupération des comptes externes possibles
		this.prestationService.listeCompteExterne().pipe(first()).subscribe(f => {
			//Stockage des comptes externes
			this.listeCompteExterne = this.listeCompteExterne.concat(f);

			//Synchronisation du compte externe
			this.syncCompteExterne();
		});

		//Récupération des domaines taxe possibles
		this.prestationService.listeDomaineTaxe().pipe(first()).subscribe(f => {
			//Stockage des domaines taxe
			this.listeDomaineTaxe = this.listeDomaineTaxe.concat(f);

			//Synchronisation du domaine taxe
			this.syncDomaineTaxe();
		});

		//Chargement des données de la page
		this.loadPageData();

		//Abonnement au rechargement des données de la page
		this.souscription = this.reloadPageDataEvent?.subscribe(() => this.loadPageData());
	}

	/**
	 * Chargement des données de la page
	 */
	loadPageData() {
		//Liste des cumuls possibles
		this.listeCumul = [this.cumulAucun];

		//Récupération des cumuls possibles
		this.prestationService.listeCumul().pipe(first()).subscribe(f => {
			//Stockage des cumuls
			this.listeCumul = this.listeCumul.concat(f.map(c => {
				c.display = `${c.libelle} (${this.translateService.instant("admin.frais.prestations.detail.reglesGestion.controles.cumul." + (c.type == TypeCumul.TYPE_JOURNALIER ? "journalier" : "evenement"))})`;
				return c;
			}));

			//Synchronisation du cumul
			this.syncCumul();
		});
	}

	/**
	 * Synchronisations lors de la mise à jour de la prestation
	 */
	private syncPrestation() {
		//Synchronisation de l'unité
		this.syncUnite();

		//Synchronisation du domaine prestation
		this.syncDomainePrestation();

		//Synchronisation du cumul
		this.syncCumul();

		//Synchronisation de la date d'application
		this.syncDateApplication();

		//Synchronisation du montant
		this.syncMontant();

        //Cohérence barème
        this.checkBareme();
	}

	/**
	 * Synchronisation de l'unité
	 */
	private syncUnite() {
		//Si aucune unité n'est renseignée
		if (!this.prestation?.unite) {
			//On choisit la premiere unité
			this.prestation.unite = this.listeUnite[0];
		}
	}

	/**
	 * Synchronisation du domaine prestation
	 */
	private syncDomainePrestation() {
		//Si un domaine prestation est renseigné
		if (this.prestation?.idDomainePrestation) {
			//On choisit le domaine prestation
			this.selectedDomainePrestation = this.listeDomainePrestation.find(f => f.id === this.prestation.idDomainePrestation);
		} else {
			//Sinon, on choisit le premier domaine prestation
			this.selectedDomainePrestation = this.listeDomainePrestation[0];
		}
	}

	/**
	 * Synchronisation du cumul
	 */
	private syncCumul() {
		//Si un cumul est renseigné
		if (this.prestation?.idCumul) {
			//On choisit le cumul
			this.selectedCumul = this.listeCumul.find(f => f.idCumul === this.prestation.idCumul);
		} else {
			//Sinon, on choisit le premier cumul
			this.selectedCumul = this.listeCumul[0];
		}
	}

	/**
	 * Synchronisation du compte externe
	 */
	private syncCompteExterne() {
		//Si un compte externe est renseigné
		if (this.prestation?.compteExterne) {
			//On choisit le compte externe
			this.selectedCompteExterne = this.listeCompteExterne.find(f => f.numero === this.prestation.compteExterne);
		} else {
			//Sinon, forçage du premier compte
			this.selectedCompteExterne = this.listeCompteExterne[0];
		}
	}

	/**
	 * Synchronisation du domaine taxe
	 */
	private syncDomaineTaxe() {
		//Si un domaine taxe est renseigné
		if (this.prestation?.idAppTva) {
			//On choisit le domaine taxe
			this.selectedDomaineTaxe = this.listeDomaineTaxe.find(f => f.idDomaine === this.prestation.idAppTva);
		} else {
			//Sinon, forçage du premier domaine taxe
			this.selectedDomaineTaxe = this.listeDomaineTaxe[0];
		}

		//Si un domaine alternatif est renseigné
		if (this.prestation?.idAppTvaBis) {
			//On choisit le domaine alternatif
			this.selectedDomaineAlternatif = this.listeDomaineTaxe.find(f => f.idDomaine === this.prestation.idAppTvaBis);
		} else {
			//Sinon, forçage du premier domaine alternatif
			this.selectedDomaineAlternatif = this.listeDomaineTaxe[0];
		}
	}

	/**
	 * Synchronisation de la date d'application
	 */
	private syncDateApplication() {
		//On met la date d'application en cache
		this.dateApplicationCached = this.prestation.dateApplication;

		//Si le type de la prestation est REEL
		if (this.prestation?.type == TypePrestation.REEL || !!this.prestation?.idDomainePrestation) {
			//Forçage de la date d'application à undefined
			this.prestation.dateApplication = undefined;
		}
	}

	/**
	 * Synchronisation du montant
	 */
	private syncMontant() {
		//On met le montant en cache
		this.montantCached = this.prestation.montant;

		//Si le type de la prestation diffère de PLAFOND
		if (this.prestation.type == TypePrestation.REEL || !!this.prestation?.idDomainePrestation) {
			//Forçage du montant unitaire à undefined
			this.prestation.montant = undefined;

			//Forçage de la devise à EUR
			this.prestation.devise = "EUR";
		}
	}

	/**
	 * Mise à jour des autres éléments lors de la mise à jour de l'unité
	 */
	onUniteChange() {
		//Réinitialisation des champs concernés
		this.prestation.quantiteObligatoire = false;
		this.prestation.quantiteModifiable = false;
		this.prestation.quantiteDefaut = 1.0;

		//Contrôle du champ barème
		this.checkBareme();

		//Contrôle du champ géolocalisation
		this.checkGeolocalisation();
		this.onGeolocationChange();

		//Émission de l'évènement de mise à jour des données imbriquées
		this.prestationDetailService.triggerNestedDataUpdate();
	}

	/**
	 * Mise à jour du domaine prestation dans l'objet suite à une sélection
	 */
	onDomainePrestationChange() {
		//Si l'ID est 0 (choix < Aucun >)
		if (this.selectedDomainePrestation?.id === 0) {
			//Mise à jour du domaine prestation
			this.prestation.idDomainePrestation = 0;
		} else {
			//Sinon, obtention de l'id
			const id: number = this.selectedDomainePrestation?.id?.toString() == "objetTampon" ? 0 : this.selectedDomainePrestation?.id;

			//Puis, mise à jour du domaine prestation
			this.prestation.idDomainePrestation = id;
		}

		//Contrôle du champ date d'application
		this.checkDateApplication();

		//Contrôle du champ montant unitaire
		this.checkMontant();
	}

	/**
	 * Mise à jour des autres éléments lors de la mise à jour du type
	 */
	onTypeChange() {
		//Contrôle du champ barème
		this.checkBareme();

		//Contrôle du champ prorata
		this.checkProrata();

		//Contrôle du champ date d'application
		this.checkDateApplication();

		//Contrôle des champs cumul et déplafonnement
		this.checkCumulDeplaf();

		//Contrôle du champ domaine de prestation
		this.checkDomainePrestation();

		//Contrôle du champ montant
		this.checkMontant();

		//Contrôle du champ unité
		this.checkUnite();
	}

	/**
	 * Mise à jour des autres éléments lors de la mise à jour du barème
	 */
	onBaremeChange() {
		//Si le barème est autre que SANS
		if (this.prestation.typeBareme != TypeBareme.SANS) {
			//Réinitialisation des champs concernés
			this.prestation.prorataRemb = 100.00;
			this.prestation.dateApplication = undefined;
			this.prestation.montant = undefined;
            this.prestation.bareme = true;
		}
        else {
            this.prestation.bareme = false;
        }
	}

	/**
	 * Mise à jour du cumul dans l'objet suite à une sélection
	 */
	onCumulChange() {
		//Si le compte charge sélectionné est null
		if (this.selectedCumul == null || this.selectedCumul?.libelle == null) {
			//Purge du cumul
			this.prestation.idCumul = null;
			this.prestation.prestCumul = null;
		} else {
			//Sinon, mise à jour du cumul
			this.prestation.idCumul = this.selectedCumul.idCumul;
			this.prestation.prestCumul = this.selectedCumul;
		}
	}

	/**
	 * Changement de devise
	 *
	 * @param devise Code de la devise sélectionnée
	 */
	onDeviseChange(devise: string) {
		//Mise à jour de la devise sur la prestation
		this.prestation.devise = devise;
	}

	/**
	 * Changement de géolocalisation
	 */
	onGeolocationChange() {
		//Sortie du flux standard pour forcer la prise en compte du nouveau statut du booléen
		setTimeout(() => {
			//Si la géolocalisation est false
			if (!this.prestation.geolocalisation) {
				//Forçage du trajet à false
				this.prestation.saveTrajet = false;

				//Forçage de la gestion des étapes à false
				this.prestation.gestionEtapes = false;
			}

			//Vérification de la gestion des étapes
			this.onGestionEtapesChange();
		});
	}

	/**
	 * Changement de la gestion des étapes
	 */
	onGestionEtapesChange() {
		//Sortie du flux standard pour forcer la prise en compte du nouveau statut du booléen
		setTimeout(() => {
			//Si la gestion des étapes est false
			if (!this.prestation.gestionEtapes) {
				//Forçage de la gestion du temps à false
				this.prestation.gestionTempsGlobale = false;

				//Forçage de la gestion des étapes personnelles à false
				this.prestation.etapesPersonnelles = false;
			}

			//Vérification de la gestion du temps globale
			this.onGestionTempsGlobaleChange();
		});
	}

	/**
	 * Changement de la gestion du temps globale
	 */
	onGestionTempsGlobaleChange() {
		//Sortie du flux standard pour forcer la prise en compte du nouveau statut du booléen
		setTimeout(() => {
			//Si la gestion du temps globale est false
			if (!this.prestation.gestionTempsGlobale) {
				//Forçage de la gestion du temps détaillée à false
				this.prestation.gestionTempsDetaillee = false;
			}
		});
	}

	/**
	 * Mise à jour du compte externe dans l'objet suite à une sélection
	 */
	onCompteExterneChange() {
		//Si le compte externe sélectionné est null
		if (this.selectedCompteExterne == null || this.selectedCompteExterne.numero == null) {
			//Purge du compte externe
			this.prestation.compteExterne = null;
			this.prestation.compteExterneLibelle = null
		} else {
			//Sinon, mise à jour du compte externe
			this.prestation.compteExterne = this.selectedCompteExterne.numero;
			this.prestation.compteExterneLibelle = this.selectedCompteExterne.libelle
		}
	}

	/**
	 * Mise à jour des autres éléments lors de la mise à jour du genre
	 */
	onGenreChange() {
		//Si le genre est STANDARD
		if (this.prestation.genre == Genre.STANDARD) {
			//Réinitialisation des champs concernés
			this.selectedCompteExterne = this.listeCompteExterne[0];
			this.prestation.compteExterne = undefined;
			this.prestation.modeParticipant = ModeParticipants.TOUS;
			this.prestation.participation = false;
		}
	}

	/**
	 * Mise à jour du domaine taxe dans l'objet suite à une sélection
	 */
	onDomaineTaxeChange() {
		//Si l'ID est 0 (choix < Aucun >)
		if (this.selectedDomaineTaxe?.idDomaine === 0) {
			//Mise à jour du domaine taxe
			this.prestation.idAppTva = 0;
		} else {
			//Sinon, obtention de l'id
			const id: number = this.selectedDomaineTaxe?.idDomaine?.toString() == "objetTampon" ? 0 : this.selectedDomaineTaxe?.idDomaine;

			//Puis, mise à jour du domaine taxe
			this.prestation.idAppTva = id;
		}

		//Réinitialisation des autres champs
		this.prestation.saisieTaxe = false;
		this.prestation.idAppTvaBis = 0;
		this.selectedDomaineAlternatif = this.listeDomaineTaxe[0];
	}

	/**
	 * Mise à jour du domaine alternatif dans l'objet suite à une sélection
	 */
	onDomaineAlternatifChange() {
		//Si l'ID est 0 (choix < Aucun >)
		if (this.selectedDomaineAlternatif?.idDomaine === 0) {
			//Mise à jour du domaine alternatif
			this.prestation.idAppTvaBis = 0;
		} else {
			//Sinon, obtention de l'id
			const id: number = this.selectedDomaineAlternatif?.idDomaine?.toString() == "objetTampon" ? 0 : this.selectedDomaineAlternatif?.idDomaine;

			//Puis, mise à jour du domaine alternatif
			this.prestation.idAppTvaBis = id;
		}
	}

	/**
	 * Contrôle du champ barème
	 */
	private checkBareme() {
		//Si le type n'est pas FORFAIT ou que le type distance n'est pas KM ou Miles
		if (this.prestation.type != TypePrestation.FORFAIT || !(this.prestation.unite?.typeDistance == TypeDistance.TYPE_DISTANCE_KM || this.prestation.unite?.typeDistance == TypeDistance.TYPE_DISTANCE_MILES)) {
			//Forçage du barème à 'Sans'
			this.prestation.typeBareme = TypeBareme.SANS;
		}

        //Si sans type de barème alors barème = false, sinon true
        this.prestation.bareme = this.prestation.typeBareme != TypeBareme.SANS;
	}

	/**
	 * Contrôle du champ prorata
	 */
	private checkProrata() {
		//Si le type de la prestation n'est pas REEL ou que le type de la prestation est FORFAIT et que le barème est SANS
		if (!(this.prestation.type == TypePrestation.REEL || (this.prestation.type == TypePrestation.FORFAIT && this.prestation.typeBareme == TypeBareme.SANS))) {
			//Forçage du prorata à 100%
			this.prestation.prorataRemb = 100.00;
		}
	}

	/**
	 * Contrôle du champ date d'application
	 */
	private checkDateApplication() {
		//Si le type de la prestation est REEL
		if (this.prestation.type == TypePrestation.REEL || this.selectedDomainePrestation.id != 0) {
			//Forçage de la date d'application à undefined
			this.prestation.dateApplication = undefined;
		} else if (this.isShowMontantButton() && this.prestation.dateApplication == null) {
			//Si on doit afficher le bouton montant et que la date d'application est null
			//On réapplique la date au chargement de la prestation
			this.prestation.dateApplication = this.dateApplicationCached;
		}
	}

	/**
	 * Contrôle des champs cumul et déplafonnement
	 */
	private checkCumulDeplaf() {
		//Si le type de la prestation diffère de PLAFOND
		if (this.prestation.type != TypePrestation.PLAFOND) {
			//Forçage du cumul à aucun
			this.prestation.idCumul = undefined;
			this.prestation.prestCumul = null;
			this.selectedCumul = this.listeCumul[0];

			//Forçage du déplafonnement à false
			this.prestation.disableDeplaf = false;
		}
	}

	/**
	 * Contrôle des champs domaine de prestation et montant unitaire
	 */
	private checkDomainePrestation() {
		//Si le type de la prestation diffère de PLAFOND
		if (this.prestation.type == TypePrestation.REEL) {
			//Forçage du domaine de prestation à aucun
			this.selectedDomainePrestation = this.listeDomainePrestation[0];
			this.prestation.idDomainePrestation = 0;
		}
	}

	/**
	 * Contrôle du champ montant unitaire
	 */
	private checkMontant() {
		//Si le type de la prestation diffère de PLAFOND
		if (this.prestation.type == TypePrestation.REEL || this.selectedDomainePrestation.id != 0) {
			//Forçage du montant unitaire à undefined
			this.prestation.montant = undefined;

			//Forçage de la devise à EUR
			this.prestation.devise = "EUR";
		} else if (this.isShowMontantButton() && this.prestation.montant == null) {
			//Si on doit afficher le bouton montant et que le montant est null
			//On réapplique le montant au chargement de la prestation
			this.prestation.montant = this.montantCached;
		}
	}

	/**
	 * Contrôle du champ géolocalisation
	 */
	private checkGeolocalisation() {
		//Si le type de l'unité est NONE
		if (this.prestation.unite?.typeDistance == TypeDistance.TYPE_DISTANCE_NONE) {
			//Forçage de la géolocalisation à false
			this.prestation.geolocalisation = false;
		}
	}

	/**
	 * Contrôle du champ unité
	 */
	private checkUnite() {
		//Si le type de prestation est soit forfait, soit plafond
		if (this.prestation.type == TypePrestation.FORFAIT || this.prestation.type == TypePrestation.PLAFOND) {
			//Purge de la valeur
			this.prestation.unite = undefined;

			//Suppression du choix < Aucun > de la liste de choix possibles
			this.listeUnite = this.listeUnite.filter(u => u.idUnite != 0);
		} else {
			//Sinon intégration du choix < Aucun > dans la liste de choix possibles
			this.listeUnite = [this.uniteAucune].concat(this.listeUnite.filter(u => u.idUnite != 0));

			//Puis sélection de ce choix par défaut
			this.prestation.unite = this.listeUnite[0];
		}
	}

	/**
	 * Fonction de callback pour l'ajout d'un cumul
	 */
	addCumulClick: () => void = () => {
		//Ouverture de la boîte de dialogue pour ajouter un cumul
		this.matDialog.open(AddCumulComponent, {
			width: '60%'
		}).afterClosed().subscribe(cumul => {
			if (cumul?.libelle) {
				//Ajout du cumul
				this.listeCumul.push(cumul);

				//Sélection du cumul
				this.selectedCumul = this.listeCumul.find(f => f.libelle === cumul.libelle);

				//Rafraichissement du cumul
				this.onCumulChange();
			}
		});
	}

	/**
	 * Fonction de callback pour l'ajout d'un compte externe
	 */
	addCompteExterneClick: () => void = () => {
		//Ouverture de la boîte de dialogue pour ajouter un compte externe
		this.matDialog.open(AddCompteExterneComponent, {
			width: '60%'
		}).afterClosed().subscribe(compte => {
			if (compte?.numero) {
				//Ajout du compte externe
				this.listeCompteExterne.push(compte);

				//Sélection du compte ajouté
				this.selectedCompteExterne = this.listeCompteExterne.find(f => f.numero === compte.numero);

				//Rafraichissement du compte externe
				this.onCompteExterneChange();
			}
		});
	}

	/**
	 * Détermine la possibilité ou non de sélectionner des barèmes fiscaux/SNCF
	 */
	get isBaremeWithSelection(): boolean {
		//Avoir une prestation de type "Forfait"
		return this.prestation?.type == TypePrestation.FORFAIT
				//Avoir une unité de type Km ou Miles
				&& (this.prestation?.unite?.typeDistance == TypeDistance.TYPE_DISTANCE_KM
						|| this.prestation?.unite?.typeDistance == TypeDistance.TYPE_DISTANCE_MILES)
				//Avoir aucun domaine de prestation
				&& !this.prestation?.idDomainePrestation
				//Sélectionner un barème fiscal ou SNCF
				&& (this.prestation?.typeBareme == TypeBareme.BAREME_FISCAL
						|| this.prestation?.typeBareme == TypeBareme.BAREME_SNCF);
	}

	/**
	 * Ouverture de la popin des barèmes
	 */
	openBaremePopin() {
		//Ouverture de la boîte de dialogue des barèmes
		this.matDialog.open(BaremePopinComponent, {
			width: '60%',
			panelClass: 'mat-dialog-without-margin',
			data: {
				idPrestation: this.prestation.idPrestation,
				typeBareme: this.prestation.typeBareme,
				unite: this.prestation.unite.libelle
			}
		});
	}

	/**
	 * Ouverture de la popin des montants
	 */
	onOpenMontant() {
		//Ouverture de la boîte de dialogue des montants
		this.matDialog.open(MontantPopinComponent,{
			width: '60%',
			panelClass: 'mat-dialog-without-margin',
			data: {
				idPrestation: this.prestation.idPrestation,
				devise: this.prestation.devise
			}
		}).afterClosed().subscribe((montant: Montant) => {
			//Gestion du cas où la popin est fermée sans validation
			if (!montant) {
				return;
			}

			//Mise à jour du montant (et de sa valeur cache) si une valeur est renvoyée
			if (montant.taux != null) {
				this.montantCached = montant.taux;
				this.prestation.montant = montant.taux;
			}

			//Mise à jour de la date d'application si une valeur est renvoyée
			if (montant.dateApplication != null) {
				this.dateApplicationCached = montant.dateApplication;
				this.prestation.dateApplication = montant.dateApplication;
			}
		});
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy() {
		//Résiliation de la souscription
		this.souscription?.unsubscribe();
	}

	/**
	 * Indicateur d'affichage du bouton montant
	 */
	isShowMontantButton(): boolean {
		//Le bouton est affiché quand:
		//Je suis dans une prestation de type Plafond ou Forfait.
		//Le champs Domaine de Prestation est sur "Aucun"
		//Le champs Barème est sur "Sans"
		return (this.prestation.type == TypePrestation.PLAFOND || this.prestation.type == TypePrestation.FORFAIT)
			&& (this.prestation.idDomainePrestation === 0 || this.prestation.idDomainePrestation == null)
			&& this.prestation.typeBareme === TypeBareme.SANS;
	}
}